import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16" data-testid="hero-section">
      <motion.div style={{ y, opacity }} className="max-w-6xl mx-auto text-center relative z-10">
        <div className="flex justify-center mb-10 overflow-hidden">
          <motion.span
            data-testid="hero-badge"
            initial={{ y: "120%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, delay: 2.35, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-xs tracking-[0.25em] uppercase glass border border-[#10E59A]/30 text-[#10E59A]"
          >
            <Sparkles className="w-3.5 h-3.5" />
            AI Automation Studio — London
          </motion.span>
        </div>

        <h1
          data-testid="hero-heading"
          className="font-['Outfit'] font-light text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.98] text-[#E8F1F5]"
        >
          <MaskedLine delay={2.45}>Intelligence</MaskedLine>
          <MaskedLine delay={2.58}>
            <span className="text-outline font-medium">beneath the</span>
          </MaskedLine>
          <MaskedLine delay={2.71}>
            <span className="font-bold gradient-text text-glow">surface.</span>
          </MaskedLine>
        </h1>

        <div className="overflow-hidden mt-10">
          <motion.p
            data-testid="hero-subtitle"
            initial={{ y: "120%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay: 2.85, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-2xl mx-auto text-base md:text-lg leading-relaxed text-[#8AA1B1]"
          >
            Ocean Mind AI builds autonomous systems that work while you sleep — AI agents,
            workflow automation, and intelligent pipelines engineered for companies ready to
            move faster than the tide.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 3.05 }}
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollToId("#contact")}
            className="group rounded-full px-9 py-4 text-sm font-semibold bg-[#00F2FE] text-[#020813] hover:bg-[#10E59A] hover:shadow-[0_0_45px_-8px_rgba(16,229,154,0.7)] transition-[background-color,box-shadow] duration-300 flex items-center gap-2"
          >
            Automate Your Business
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollToId("#services")}
            className="rounded-full px-9 py-4 text-sm font-medium bg-[#00F2FE]/10 text-[#00F2FE] border border-[#00F2FE]/40 hover:bg-[#00F2FE]/25 transition-colors duration-300"
          >
            Explore Services
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 3.4 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="text-[#8AA1B1]/70 text-[10px] tracking-[0.4em] uppercase">Scroll to descend</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[#00F2FE] to-transparent"
        />
      </motion.div>
    </section>
  );
};
